import { useMemo } from "react";
import {
  Card,
  Label,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Typography,
} from "@altimateai/lego";
import dayjs from "dayjs";
import { DateRange } from "@/components/dateRange";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { FlexListSummary } from "@/components/listSummary";
import { formatNumber } from "@/uiCore";
import { useAppState } from "@/modules/app/useAppContext";
import { useComputeEfficiency } from "@/pages/sku/hooks";
import { BreakdownChart } from "@/pages/sku/components";
import { WorkspacesCostTable } from "@/pages/workspaces/components";
import { useWorkspaces } from "@/pages/workspaces/hooks";

type BreakdownTab = "sku" | "product";

const DatabricksCostBreakdown = () => {
  useBreadcrumb("Breakdown");
  const { currency } = useAppState();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();

  const breakdownTabParam = searchParams.get("breakdownTab");
  const breakdownTab: BreakdownTab = breakdownTabParam === "product" ? "product" : "sku";

  const { startDate, endDate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );
  const startDateStr = dayjs(startDate).format("YYYY-MM-DD");
  const endDateStr = dayjs(endDate).format("YYYY-MM-DD");

  const { data: workspacesList, isLoading: isWorkspacesLoading } = useWorkspaces();
  const { data: efficiencyData, isLoading: isEfficiencyLoading } = useComputeEfficiency({
    start_date: startDateStr,
    end_date: endDateStr,
  });

  const activeWorkspaces = workspacesList?.workspaces?.length ?? 0;

  const summary = useMemo(
    () => [
      {
        label: "Total Cost",
        value: formatNumber(efficiencyData?.total_cost ?? 0, {
          currency,
          notation: "standard",
        }),
      },
      {
        label: "Total DBUs",
        value: (efficiencyData?.total_dbus ?? 0).toLocaleString(undefined, {
          maximumFractionDigits: 0,
        }),
      },
      {
        label: "Active Workspaces",
        value: String(activeWorkspaces),
      },
    ],
    [efficiencyData, activeWorkspaces, currency]
  );

  return (
    <Stack direction="column" gap="xl">
      <Stack justify="between" align="center">
        <Typography variant="page-heading">Breakdown</Typography>
        <Stack direction="row" align="center" gap="md">
          <Label className="al-m-0">Time Period:</Label>
          <DateRange
            startDate={startDate}
            endDate={endDate}
            availableStaticRanges={[
              "Last 7 days",
              "Last 28 days",
              "Last 3 months",
              "Last 6 months",
              "Last 12 months",
            ]}
            onDateRangeSelect={(newStartDate, newEndDate) => {
              updateSearchParamsByKey({
                start_date: newStartDate?.toISOString(),
                end_date: newEndDate?.toISOString(),
              });
            }}
          />
        </Stack>
      </Stack>

      <FlexListSummary
        summary={summary}
        isLoading={isEfficiencyLoading || isWorkspacesLoading}
        skeletonCount={3}
      />

      <WorkspacesCostTable startDate={startDateStr} endDate={endDateStr} />

      <Card className="al-p-4" style={{ borderColor: "#D5DBE4" }}>
        <Stack direction="column" gap="md">
          <Typography variant="subheading" weight="medium">
            Cost Breakdown
          </Typography>
          <Tabs
            variant="underlined"
            value={breakdownTab}
            onValueChange={(value: string) => updateSearchParamsByKey({ breakdownTab: value })}
          >
            <TabsList className="al-mb-2">
              <TabsTrigger value="sku">By SKU</TabsTrigger>
              <TabsTrigger value="product">By Product</TabsTrigger>
            </TabsList>
            {/* forceMount both tabs so queries fire in parallel; hidden via CSS. */}
            <TabsContent value="sku" forceMount hidden={breakdownTab !== "sku"}>
              <BreakdownChart startDate={startDateStr} endDate={endDateStr} groupBy="sku" noCard />
            </TabsContent>
            <TabsContent value="product" forceMount hidden={breakdownTab !== "product"}>
              <BreakdownChart
                startDate={startDateStr}
                endDate={endDateStr}
                groupBy="product"
                noCard
              />
            </TabsContent>
          </Tabs>
        </Stack>
      </Card>
    </Stack>
  );
};

export default DatabricksCostBreakdown;
